/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DepartmentDetailComponent } from '@departments/department-detail/department-detail.component';
import { DepartmentsService } from '@departments/services/departments.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { Department } from '@shared/models';
import { extractConstraintViolations } from '@shared/utils';
import { merge, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import * as DepartmentsActions from './departments.actions';

@Injectable()
export class DepartmentsEffects {
  constructor(
    private actions$: Actions<DepartmentsActions.DepartmentsActions>,
    private departmentsService: DepartmentsService,
    private translateService: TranslateService,
    private snackBarService: MatSnackBar,
    private dialogService: MatDialog
  ) { }

  loadDepartments$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.LOAD_DEPARTMENTS),
    switchMap(() => this.departmentsService.getDepartments().pipe(
      map(departments => new DepartmentsActions.LoadDepartmentsSuccess(departments)),
      catchError(() => of(new DepartmentsActions.LoadDepartmentsFailure()))
    ))
  ));

  loadDepartmentsFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.LOAD_DEPARTMENTS_FAILURE),
    switchMap(() => this.translateService.get('departments.loadDepartmentsError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  editDepartment$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.EDIT_DEPARTMENT),
    map(action => action.departmentId),
    tap(departmentId => this.dialogService.open(DepartmentDetailComponent, {
      width: '25rem',
      data: { departmentId }
    }))
  ), { dispatch: false });

  loadDepartment$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.LOAD_DEPARTMENT),
    map(action => action.departmentId),
    switchMap(departmentId => {
      const getDepartment$ = of(departmentId);

      const createDepartment$ = getDepartment$.pipe(
        filter(id => !id),
        switchMap(() => of({ } as Department))
      );

      const loadDepartment$ = getDepartment$.pipe(
        filter(id => !!id),
        switchMap(id => this.departmentsService.getDepartment(id))
      );

      return merge(createDepartment$, loadDepartment$).pipe(
        map(department => new DepartmentsActions.LoadDepartmentSuccess(department)),
        catchError(() => of(new DepartmentsActions.LoadDepartmentFailure()))
      );
    })
  ));

  loadDepartmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.LOAD_DEPARTMENT_FAILURE),
    switchMap(() => this.translateService.get('departments.loadDepartmentError').pipe(
      tap(errorMessage => this.snackBarService.open(errorMessage))
    ))
  ), { dispatch: false });

  persistDepartment$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.PERSIST_DEPARTMENT),
    map(action => action.department),
    switchMap(department => {
      const persistDepartment$ = of(department['@id']);

      const createDepartment$ = persistDepartment$.pipe(
        filter(id => !id),
        switchMap(() => this.departmentsService.createDepartment(department))
      );

      const updateDepartment$ = persistDepartment$.pipe(
        filter(id => !!id),
        switchMap(() => this.departmentsService.updateDepartment(department))
      );

      return merge(createDepartment$, updateDepartment$).pipe(
        map(() => new DepartmentsActions.PersistDepartmentSuccess()),
        catchError((error: HttpErrorResponse) =>
          of(new DepartmentsActions.PersistDepartmentFailure(firstViolationMessage(error)))
        )
      );
    })
  ));

  persistDepartmentSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.PERSIST_DEPARTMENT_SUCCESS),
    tap(() => this.dialogService.closeAll()),
    map(() => new DepartmentsActions.LoadDepartments())
  ));

  persistDepartmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.PERSIST_DEPARTMENT_FAILURE),
    switchMap(action => this.translateService.get('departments.persistDepartmentError').pipe(
      tap(errorMessage => this.snackBarService.open(action.message ?? errorMessage))
    ))
  ), { dispatch: false });

  deleteDepartment$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.DELETE_DEPARTMENT),
    map(action => action.departmentId),
    switchMap(departmentId => this.departmentsService.deleteDepartment(departmentId).pipe(
      switchMap(() => [
        new DepartmentsActions.DeleteDepartmentSuccess(),
        new DepartmentsActions.LoadDepartments()
      ]),
      catchError((error: HttpErrorResponse) =>
        of(new DepartmentsActions.DeleteDepartmentFailure(firstViolationMessage(error)))
      )
    ))
  ));

  /**
   * Beim Loeschen ist die Meldung des Backends die eigentliche Information (etwa
   * "Dieser Abteilung sind noch 3 aktive Personen zugeordnet"), deshalb wird sie
   * bevorzugt angezeigt und der generische Text nur als Rueckfallebene genutzt.
   */
  deleteDepartmentFailure$ = createEffect(() => this.actions$.pipe(
    ofType(DepartmentsActions.DELETE_DEPARTMENT_FAILURE),
    switchMap(action => this.translateService.get('departments.errors.delete').pipe(
      tap(errorMessage => this.snackBarService.open(action.message ?? errorMessage))
    ))
  ), { dispatch: false });
}

/**
 * Holt die erste Validierungsmeldung aus einer API-Platform-Fehlerantwort (422).
 */
function firstViolationMessage(error: HttpErrorResponse): string | undefined {
  return extractConstraintViolations(error)[0]?.message;
}
